// Okno Seznam: ribbon z příkazů, hledání, filtry, tabulka, stránkování, živá aktualizace. Žádný JavaScript aplikace.
import { action, core, runCommand, ApiError } from '../api';
import type { CommandDef, ListDataRequest, ListDataResponse, ListDescriptor, CommandResult, FilterDef, WindowDescriptor } from '../contract';
import { h, clear, debounce, asObject, isTyping } from '../dom';
import { t, tk } from '../i18n';
import { events } from '../events';
import { IwRibbon, type RibbonButton } from '../components/ribbon';
import { IwTable, rowsFromDrag, type ColumnState, type Row } from '../components/table';
import { IwCards } from '../components/cards';
import { IwMenu, type MenuItem } from '../components/menu';
import { confirm, dialog } from '../components/dialog';
import { toast } from '../components/toast';
import { errorState, spinner, statusBar, emptyState, IwSplit } from '../components/misc';
import { IwCommandBar, barMenuItem, headButton, fillHref, type BarItem } from '../components/commandbar';
import { popover } from '../components/ribbon';
import { savePref } from '../theme';
import type { IwWindow, WindowView } from '../wm/window';
import type { WindowManager } from '../wm/manager';
import { createView, type ViewHost } from './factory';
import { PagedRows } from '../components/paged';
import { icon } from '../icons';

interface ListState {
  page: number;
  sort: { col: string; dir: 'asc' | 'desc' } | null;
  search: string;
  filters: Record<string, unknown>;
}

export class ListView implements WindowView {
  readonly el: HTMLElement;
  private ribbon = new IwRibbon();
  private table = new IwTable(); // režim výběru výchozí vypnutý i na dotyku, zapíná se na kartě Zobrazení (rozhodnutí 25. 10. 2026)
  private searchInput = h('input', { class: 'input search', type: 'search', placeholder: t('core.ui.search_placeholder'), 'aria-label': t('core.cmd.search') });
  private chips = h('div', { class: 'filter-chips' });
  private status = h('div', { class: 'list-status' });
  private content = h('div', { class: 'list-content' });
  private state: ListState;
  private total = 0;
  private selection: string[] = [];
  private unsubscribe: (() => void) | null = null;
  private loading = false;
  private colState: Record<string, ColumnState> = {};
  private colKey: string;
  private refreshSoon = debounce(() => void this.load(true), 300);
  private saveCols = debounce(() => void core('prefs', 'set', { key: this.colKey, value: this.colState }).catch(() => undefined), 800);
  // vrstvy (levý panel) a dlaždice – obecné pro každý seznam, který je deklaruje
  private cards = new IwCards();
  private viewMode: 'list' | 'cards' = 'list';
  private side = h('div', { class: 'list-side' });
  private split: IwSplit | null = null;
  private hiddenLayers = new Set<string>();
  private sideHidden = false;
  private narrow = false;
  private ro: ResizeObserver | null = null;
  private describeSoon = debounce(() => void this.refreshLayers(), 400);
  /** v konzoli: vlevo je strom – vrstvy nejsou panel, ale tlačítko s nabídkou ve skupině Filtr */
  private embedded: boolean;
  /** vizitka odvozená ze sloupců (okno žádnou nedeklaruje) */
  private autoCard = false;
  /** v záložce záznamu: řádek nástrojů (jen karta Domů + Obnovit), bez karty Zobrazení */
  private inline: boolean;
  // ---- ovládání 'bar' (koncepce A): záhlaví okna s lištou příkazů, štítky filtrů nad seznamem, detail vpravo, telefon
  private bar: IwCommandBar | null = null;
  private filterRow = h('div', { class: 'list-filters' });
  private detailSplit: IwSplit | null = null;
  private detailPane = h('div', { class: 'list-detail' });
  private detailView: WindowView | null = null;
  private detailId: string | null = null;
  private detailSeq = 0;
  private detailOn = false;
  private fab = h('button', { type: 'button', class: 'fab' });
  private selBar = h('div', { class: 'sel-bar' });
  private searchOpen = false;
  /** kroky pro zpět na telefonu (wm.pushBack): režim výběru, hledání, vysunutý panel */
  private backSel = 0;
  private backSearch = 0;
  private backSide = 0;
  /** akce detailu v podokně (Upravit, Smazat… / Uložit, Zahodit změny) – v záhlaví okna místo vlastní lišty */
  private paneItems: BarItem[] | null = null;
  /** popisky rychlých akcí na vizitce detailu (Napsat e-mail, Hotovo…) – na liště seznamu je už neukazovat */
  private paneQuick: string[] = [];
  private barBase: BarItem[] = [];
  private showDetailSoon = debounce(() => void this.syncDetail(), 120);
  /** plynulý seznam (bar): řádky po dávkách, dočítání při posouvání místo stránkování (společné s Poštou) */
  private paged: PagedRows<Row> | null = null;
  private loadSeq = 0;
  /** výměna detailu právě běží (dotaz na neuložené změny) – další jen naplánovat */
  private detailBusy = false;
  private observers: ResizeObserver[] = [];
  private selAllBar = h('div', { class: 'select-all-bar' });
  private azEl = h('div', { class: 'az-index', role: 'navigation' });
  private azBubble = h('div', { class: 'az-bubble' });
  private azIndex: Record<string, number> = {};

  constructor(
    private desc: ListDescriptor,
    private win: IwWindow,
    private wm: WindowManager,
    private host: ViewHost,
  ) {
    this.state = { page: 1, sort: desc.defaultSort, search: '', filters: this.defaultFilters() };
    this.embedded = host.embedded === true || host.inline === true;
    // seznam bez vlastní vizitky (Uživatelé, Účty, Audit…): vizitka z prvních sloupců – Seznam / Tabulka / Dlaždice a na telefonu
    // dvouřádkové řádky jako v Kontaktech; na PC zůstává výchozí tabulka
    // ovládání bar i ve vložené stránce konzole (záhlaví dostane konzole); ne v záložce záznamu (inline)
    if (desc.chrome === 'bar' && host.inline !== true && !desc.card) {
      const cols = desc.columns.filter((c) => !c.hidden && c.type !== 'image' && c.type !== 'bool');
      const title = cols.find((c) => c.type === 'text') ?? cols[0]; // název (text), ne pořadí / číslo
      if (title) {
        desc.card = { title: title.key, lines: cols.filter((c) => c !== title).slice(0, 2).map((c) => c.key) };
        this.autoCard = true;
      }
    }
    if (desc.card && !desc.card.icon) desc.card.icon = desc.icon; // dlaždice bez fotky: ikona okna
    // hodnoty na vizitce podle sloupce (výčet → popisek, datum → místní formát), i u vlastní vizitky
    if (desc.chrome === 'bar') this.cards.format = (key, row) => { const c = desc.columns.find((x) => x.key === key); return c && c.type !== 'image' && c.type !== 'text' ? IwTable.cellText(c, row) : String(row[key] ?? ''); };
    this.inline = host.inline === true;
    if (this.inline) this.ribbon.forceCompact();
    this.colKey = `cols.${desc.app}.${desc.window}`;
    const prefs = (window as unknown as { iwanPrefs?: Record<string, unknown> }).iwanPrefs ?? {};
    // dlaždice: uložená volba; bez ní na dotyku (telefon) výchozí dlaždice, na PC tabulka
    const savedView = prefs[`view.${desc.app}.${desc.window}`];
    if (desc.card && (savedView === 'cards' || (savedView === undefined && window.matchMedia('(pointer: coarse)').matches))) this.viewMode = 'cards';
    if (desc.layers) {
      const hidden = prefs[`layers.${desc.app}.${desc.window}`];
      if (Array.isArray(hidden)) this.hiddenLayers = new Set(hidden.map(String));
      this.table.rowsDraggable = this.cards.rowsDraggable = desc.layers.move === true;
    }
    if (desc.chrome === 'bar' && host.inline !== true) {
      this.bar = new IwCommandBar();
      if (desc.card) {
        const saved = prefs[`viewbar.${desc.app}.${desc.window}`];
        const phone = this.wm.currentMode() === 'mobile';
        this.viewMode = saved === 'list' ? 'list' : saved === 'grid' || saved === 'rows' ? 'cards' : this.autoCard && !phone ? 'list' : 'cards';
        this.cards.setLayout(saved === 'grid' ? 'grid' : 'rows');
      }
      if (desc.layers) this.sideHidden = prefs[`side.${desc.app}.${desc.window}`] === true;
      this.cards.onLongPress = () => this.setSelectMode(true);
      this.table.onLongPress = () => this.setSelectMode(true);
    }
    if (desc.layers && !this.embedded && !this.bar) {
      this.split = new IwSplit();
      this.split.first.appendChild(this.side);
      this.split.second.appendChild(this.content);
      this.split.setWidth(220);
      this.el = h('div', { class: 'view list-view has-side' }, this.ribbon, this.split, this.status);
      if (this.bar) this.split.setFirstHidden(this.sideHidden);
      this.renderSide();
      // úzké okno: panel schovaný, vysouvá se přes obsah (stejně jako Soubory a Kalendář)
      this.ro = new ResizeObserver(() => {
        const n = this.el.clientWidth > 0 && this.el.clientWidth < 700;
        if (!this.el.clientWidth || n === this.narrow) return;
        this.narrow = n;
        this.split?.setOverlay(n);
        this.setSide(n ? true : this.bar ? (this.prefs()[`side.${desc.app}.${desc.window}`] === true) : false);
      });
      this.ro.observe(this.el);
    } else {
      this.el = h('div', { class: 'view list-view' }, this.ribbon, this.content, this.status);
    }
    if (this.bar) this.initBar();
    else this.buildRibbon();
    this.table.emptyText = t('core.ui.empty');
    this.cards.emptyText = t('core.ui.empty');
    this.cards.selectMode = this.table.selectMode;
    this.table.onSort = (sort) => {
      this.state.sort = sort;
      this.state.page = 1;
      if (this.bar) this.buildBar(); // zaškrtnutí v ⚙ Řadit podle
      void this.load();
    };
    const onSelect = (ids: string[]) => {
      this.selection = ids;
      this.updateEnabled();
      this.renderStatus();
      if (this.bar) this.onBarSelect(ids);
    };
    const onOpen = () => {
      const cmd = this.desc.openCommand ? this.desc.commands.find((c) => c.id === this.desc.openCommand) : null;
      if (cmd) void this.execute(cmd);
    };
    this.table.onSelect = onSelect;
    this.cards.onSelect = onSelect;
    this.table.onOpen = onOpen;
    this.cards.onOpen = onOpen;
    this.table.onContext = () => this.contextItems();
    this.cards.onContext = () => this.contextItems();
    this.table.onColumnState = (s) => {
      this.colState = s;
      this.saveCols();
    };
    this.searchInput.addEventListener('input', debounce(() => {
      this.state.search = this.searchInput.value;
      this.state.page = 1;
      void this.load();
    }, 450));
    this.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        this.state.search = this.searchInput.value;
        this.state.page = 1;
        void this.load();
      }
    });
    if (!this.bar) {
      this.ribbon.right().appendChild(this.chips);
      if (desc.searchable) this.ribbon.right().appendChild(h('div', { class: 'search-wrap' }, icon('search'), this.searchInput));
    }
    this.renderChips();
    this.content.appendChild(spinner());
    void this.init();
    this.unsubscribe = events.subscribe(desc.channels, () => { this.refreshSoon(); if (this.desc.layers) this.describeSoon(); }, { live: desc.live, onReset: () => this.refreshSoon() });
  }

  private async init(): Promise<void> {
    // stav sloupců z preferencí uživatele (přišly v session bootu)
    const saved = (window as unknown as { iwanPrefs?: Record<string, unknown> }).iwanPrefs?.[this.colKey];
    if (saved && typeof saved === 'object') this.colState = saved as Record<string, ColumnState>;
    this.table.setColumns(this.desc.columns, this.colState, this.state.sort);
    this.cards.setCard(this.desc.card);
    this.content.replaceChildren(this.active());
    await this.load();
  }

  /** Aktivní zobrazení dat: tabulka nebo dlaždice (stejný výběr a příkazy). */
  private active(): IwTable | IwCards {
    return this.viewMode === 'cards' && this.desc.card ? this.cards : this.table;
  }

  private setViewMode(mode: 'list' | 'cards'): void {
    if (mode === this.viewMode) return;
    const sel = this.active().selection();
    this.viewMode = mode;
    void savePref(`view.${this.desc.app}.${this.desc.window}`, mode);
    this.content.replaceChildren(this.active());
    this.selection = sel;
    this.buildRibbon();
    void this.load();
  }

  // ---- vrstvy (levý panel)

  private setSide(hide: boolean): void {
    this.sideHidden = hide;
    this.split?.setFirstHidden(hide);
    if (this.bar) {
      this.el.classList.toggle('side-open', !hide && this.narrow);
      if (!hide && this.narrow && !this.wm.hasBack(this.backSide)) this.backSide = this.wm.pushBack(() => { this.backSide = 0; this.setSide(true); return true; }, this.win);
      if (hide && this.backSide) {
        const id = this.backSide;
        this.backSide = 0;
        this.wm.dropBack(id);
      }
    }
    else this.buildRibbon();
  }

  private prefs(): Record<string, unknown> {
    return (window as unknown as { iwanPrefs?: Record<string, unknown> }).iwanPrefs ?? {};
  }

  private renderSide(): void {
    const L = this.desc.layers;
    clear(this.side);
    if (!L) return;
    this.side.appendChild(h('h3', null, tk(L.title)));
    for (const it of L.items) {
      const cb = h('input', { type: 'checkbox', checked: !this.hiddenLayers.has(it.value) });
      cb.addEventListener('change', () => {
        if (cb.checked) this.hiddenLayers.delete(it.value);
        else this.hiddenLayers.add(it.value);
        this.saveLayers();
      });
      const label = h('label', { class: 'layer' }, cb, it.color ? h('span', { class: 'layer-dot', style: { background: it.color } }) : it.icon ? icon(it.icon) : null, h('span', { class: 'layer-name' }, it.label), it.hint ? h('span', { class: 'muted small' }, tk(it.hint)) : null);
      if (L.move && it.writable !== false) {
        // cíl přetažení řádků (tabulka i dlaždice) – stejné chování jako složky v Souborech
        label.addEventListener('dragover', (e) => {
          if (!e.dataTransfer?.types.includes('application/x-iwan-rows')) return;
          e.preventDefault();
          if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
          label.classList.add('drop');
        });
        label.addEventListener('dragleave', () => label.classList.remove('drop'));
        label.addEventListener('drop', (e) => {
          label.classList.remove('drop');
          const ids = rowsFromDrag(e);
          if (!ids) return;
          e.preventDefault();
          void this.moveToLayer(ids, it.value);
        });
      }
      this.side.appendChild(label);
    }
  }

  private saveLayers(): void {
    void savePref(`layers.${this.desc.app}.${this.desc.window}`, Array.from(this.hiddenLayers));
    this.state.page = 1;
    void this.load();
  }

  /** Vrstvy v konzoli: nabídka se zaškrtávátky (zůstane otevřená, dá se přepnout víc najednou). */
  private layersMenu(anchorEl?: HTMLElement): void {
    const L = this.desc.layers;
    const anchor = anchorEl ?? this.ribbon.querySelector<HTMLElement>('[data-id="__layers"]');
    if (!L || !anchor) return;
    const manage: MenuItem[] = this.bar ? (L.commands ?? []).map((id) => this.desc.commands.find((c) => c.id === id)).filter((c): c is CommandDef => Boolean(c)).map((c) => ({ label: tk(c.label), icon: c.icon, onSelect: () => void this.execute(c) })) : [];
    const items: MenuItem[] = L.items.map((it) => ({
      label: it.hint ? `${it.label} – ${tk(it.hint)}` : it.label,
      color: it.color ?? null,
      icon: it.color ? null : it.icon ?? null,
      checked: !this.hiddenLayers.has(it.value),
      onSelect: () => {
        if (this.hiddenLayers.has(it.value)) this.hiddenLayers.delete(it.value);
        else this.hiddenLayers.add(it.value);
        this.saveLayers();
        this.renderSide();
        if (this.bar) {
          this.renderFilterRow();
          window.setTimeout(() => this.layersMenu(this.filterRow.querySelector<HTMLElement>('[data-id="__layers"]') ?? anchor), 0);
          return;
        }
        this.buildRibbon();
        window.setTimeout(() => this.layersMenu(), 0);
      },
    }));
    IwMenu.showAt(manage.length ? [...items, { separator: true, label: '' }, ...manage] : items, anchor);
  }

  /** Přesun řádků do vrstvy (akce okna `move_layer`); okno samo rozhodne, co to pro jeho data znamená. */
  private async moveToLayer(ids: string[], value: string): Promise<void> {
    if (!ids.length) return;
    try {
      const res = await action<{ moved: number; label: string }>(this.desc.app, this.desc.window, 'move_layer', this.desc.params, { ids, value });
      toast(t('core.ui.moved_to', { name: res.label, count: res.moved }), 'ok');
      events.kick();
      void this.load();
    } catch (e) {
      const err = e as ApiError;
      toast(tk(err.message, err.params ?? undefined), 'error');
    }
  }

  /** „Přesunout do…“ bez myši (dotyk, klávesnice): dialog s výběrem zapisovatelné vrstvy. */
  private async pickLayer(): Promise<void> {
    const L = this.desc.layers;
    if (!L || !this.selection.length) return;
    const targets = L.items.filter((i) => i.writable !== false);
    if (!targets.length) return;
    let value = targets[0]?.value ?? '';
    const list = h('div', { class: 'layer-pick', role: 'radiogroup' });
    for (const it of targets) {
      const rb = h('input', { type: 'radio', name: 'layer', value: it.value, checked: it.value === value });
      rb.addEventListener('change', () => { value = it.value; });
      list.appendChild(h('label', { class: 'layer' }, rb, it.color ? h('span', { class: 'layer-dot', style: { background: it.color } }) : it.icon ? icon(it.icon) : null, h('span', { class: 'layer-name' }, it.label)));
    }
    const res = await dialog({ title: t('core.ui.move_to'), icon: 'folder', message: list, buttons: [{ id: 'cancel', label: t('core.ui.cancel') }, { id: 'ok', label: t('core.ui.ok'), primary: true }] });
    if (res.id === 'ok') await this.moveToLayer(this.selection, value);
  }

  /** Vrstvy se mohly změnit (nový adresář…): znovu popsat okno a překreslit panel. */
  private async refreshLayers(): Promise<void> {
    try {
      const d = await action<ListDescriptor>(this.desc.app, this.desc.window, 'describe', this.desc.params, {});
      if (d.layers) {
        this.desc.layers = d.layers;
        this.renderSide();
        if (this.bar) this.renderFilterRow();
        else if (this.embedded) this.buildRibbon();
      }
    } catch {
      /* panel zůstane */
    }
  }

  // ---- ribbon a příkazy

  private buildRibbon(): void {
    const buttons: RibbonButton[] = this.desc.commands.map((c) => ({
      id: c.id,
      label: tk(c.label),
      icon: c.icon,
      shortcut: c.shortcut,
      tab: c.tab,
      group: c.group,
      primary: c.primary,
      danger: c.danger,
      onClick: () => void this.execute(c),
    }));
    // filtry jako ovládací prvky ve skupině „Filtr“ (první tři vidět, další pod „Další filtry“)
    this.desc.filters.forEach((f, i) => {
      buttons.push({ id: '__f_' + f.key, label: tk(f.label), tab: 'home', group: 'core.ui.filters', control: this.filterControl(f), overflow: i >= 3 });
    });
    const L = this.embedded ? this.desc.layers : null;
    if (L) {
      const shown = L.items.filter((i) => !this.hiddenLayers.has(i.value)).length;
      const label = shown === L.items.length ? tk(L.title) : t('core.ui.layers_some', { title: tk(L.title), shown, total: L.items.length });
      buttons.push({ id: '__layers', label, icon: 'filter', tab: 'home', group: 'core.ui.filters', active: shown < L.items.length, toggle: shown < L.items.length, onClick: () => this.layersMenu() });
    }
    if (this.desc.filters.length || L) {
      buttons.push({ id: '__clear_filters', label: t('core.ui.clear_filters'), icon: 'x', tab: 'home', group: 'core.ui.filters', overflow: this.desc.filters.length > 3, onClick: () => this.clearFilters() });
    }
    // karta Zobrazení – u každého seznamu automaticky
    if (this.desc.card) {
      buttons.push({ id: '__view_list', label: t('core.ui.view_list'), icon: 'list', tab: 'view', group: 'core.grp.view', toggle: true, active: this.viewMode === 'list', onClick: () => this.setViewMode('list') });
      buttons.push({ id: '__view_cards', label: t('core.ui.view_cards'), icon: 'tiles', tab: 'view', group: 'core.grp.view', toggle: true, active: this.viewMode === 'cards', onClick: () => this.setViewMode('cards') });
    }
    if (this.desc.layers && !this.embedded) buttons.push({ id: '__side', label: t('core.ui.side_panel'), icon: 'sidebar', tab: 'view', group: 'core.grp.view', toggle: true, active: !this.sideHidden, onClick: () => this.setSide(!this.sideHidden) });
    buttons.push({ id: '__select_mode', label: t('core.ui.select_mode'), icon: 'check-square', tab: 'view', group: 'core.grp.view', toggle: true, active: this.table.selectMode, onClick: () => { this.table.setSelectMode(!this.table.selectMode); this.cards.setSelectMode(this.table.selectMode); this.buildRibbon(); } });
    buttons.push({ id: '__refresh', label: t('core.cmd.refresh'), icon: 'refresh', shortcut: 'F5', tab: 'view', group: 'core.grp.view', onClick: () => void this.load(true) });
    buttons.push({
      id: '__columns', label: t('core.ui.columns'), icon: 'columns', tab: 'view', group: 'core.grp.view',
      onClick: () => {
        const anchor = this.ribbon.querySelector<HTMLElement>('[data-id="__columns"]');
        if (anchor) IwMenu.showAt(this.table.columnMenu(), anchor);
      },
    });
    // v záložce záznamu jen řádek nástrojů: příkazy, filtry a Obnovit (karta Zobrazení odpadá)
    this.ribbon.setButtons(this.inline ? buttons.filter((b) => (b.tab ?? 'home') === 'home' || b.id === '__refresh').map((b) => (b.id === '__refresh' ? { ...b, tab: 'home' } : b)) : buttons);
    this.updateEnabled();
    this.ribbon.setMode(this.wm.currentMode());
  }

  /** Smí se příkaz provést nad aktuálním výběrem (počet + podmínka na řádky)? */
  private allowed(c: CommandDef): boolean {
    const n = this.selection.length;
    if ((c.enabled === 'selection' && n === 0) || (c.enabled === 'single' && n !== 1)) return false;
    if (c.kind === 'link' && c.href && !fillHref(c.href, this.active().selectedRows()[0] ?? {})) return false;
    if (!c.when) return true;
    const { field, values } = c.when;
    const rows = this.active().selectedRows();
    return rows.length > 0 && rows.every((r) => values.includes(r[field] as string | number | boolean));
  }

  private updateEnabled(): void {
    if (this.bar) {
      this.bar.setDisabled((b) => {
        if (b.id.startsWith('r:')) return Boolean(b.disabled); // akce detailu řídí detail
        const c = this.desc.commands.find((x) => x.id === b.id);
        if (c) return !this.allowed(c);
        if (b.id === '__move') return this.selection.length === 0 || this.active().selectedRows().some((r) => r['writable'] === false);
        return false;
      });
      this.renderSelBar();
      return;
    }
    this.ribbon.setDisabled((b) => {
      const c = this.desc.commands.find((x) => x.id === b.id);
      return c ? !this.allowed(c) : false;
    });
  }

  private contextItems(): MenuItem[] {
    const n = this.selection.length;
    // příkaz s podmínkou na řádky, který pro výběr nejde, v menu vůbec není (pás karet ho jen zašedne)
    const items: MenuItem[] = this.desc.commands
      .filter((c) => c.enabled !== 'none' && (!c.when || this.allowed(c)))
      .map((c) => ({
        id: c.id,
        label: tk(c.label),
        icon: c.icon,
        shortcut: c.shortcut,
        danger: c.danger,
        disabled: !this.allowed(c),
        onSelect: () => void this.execute(c),
      }));
    if (this.desc.layers?.move && this.desc.layers.items.some((i) => i.writable !== false)) {
      items.push({ separator: true, label: '' }, { label: t('core.ui.move_to'), icon: 'folder', disabled: n === 0, onSelect: () => void this.pickLayer() });
    }
    if (items.length) items.push({ separator: true, label: '' });
    items.push({ label: t('core.cmd.select_all'), icon: 'check', shortcut: 'Ctrl+A', onSelect: () => this.active().selectAll() });
    if (!this.desc.commands.some((c) => c.id === 'refresh')) items.push({ label: t('core.cmd.refresh'), icon: 'refresh', shortcut: 'F5', onSelect: () => void this.load(true) });
    return items;
  }

  shortcut(sc: string): boolean {
    // detail vpravo: při práci v něm (fokus, úprava) jeho zkratky – Ctrl+S, Esc, F2…; F2 nad vybraným řádkem taky
    const pane = this.detailOn ? (this.detailView as (WindowView & { shortcut?: (sc: string) => boolean }) | null) : null;
    const inPane = pane !== null && (this.detailPane.contains(document.activeElement) || this.detailPane.querySelector('.record-view.editing') !== null);
    if (pane && (inPane || sc === 'F2') && pane.shortcut?.(sc)) return true;
    if (sc === 'F2' && this.bar && !this.detailOn && this.selection.length === 1 && this.desc.detail) { // úzké okno: otevřít rovnou do úprav
      const D = this.desc.detail;
      const id = this.selection[0];
      if (id) void this.wm.open({ app: D.app ?? this.desc.app, window: D.window, params: { [D.param]: id }, modal: true, parent: this.win, restore: { edit: true }, onResult: () => this.refreshSoon() });
      return true;
    }
    if (inPane) return false; // Delete, Ctrl+A… v detailu nepatří seznamu
    if (this.bar && sc === 'Ctrl+A' && !isTyping(document.activeElement)) {
      this.active().selectAll();
      return true;
    }
    if (this.bar && sc === 'Esc' && this.selection.length && !isTyping(document.activeElement) && !(this.isMobile() && this.searchOpen)) {
      this.active().clearSelection();
      return true;
    }
    if (sc === 'Ctrl+F') {
      if (this.bar && this.wm.currentMode() === 'mobile' && !this.searchOpen) this.toggleSearch(true);
      this.searchInput.focus();
      this.searchInput.select();
      return true;
    }
    if (sc === 'F5') {
      void this.load(true);
      return true;
    }
    const cmd = this.desc.commands.find((c) => c.shortcut === sc);
    if (cmd) {
      void this.execute(cmd);
      return true;
    }
    return false;
  }

  private async execute(cmd: CommandDef): Promise<void> {
    const n = this.selection.length;
    if (!this.allowed(cmd)) return;
    if (cmd.kind === 'link' && cmd.href) {
      const url = fillHref(cmd.href, this.active().selectedRows()[0] ?? {});
      if (url) window.open(url, /^https?:/.test(url) ? '_blank' : '_self', 'noopener,noreferrer');
      return;
    }
    if (cmd.kind === 'open' && cmd.opens?.row) {
      // cíl v řádku (open_app, open_window, open_params) – např. Přehled vede z každého řádku jinam
      const row = this.active().selectedRows()[0];
      const win = row?.['open_window'];
      if (typeof win !== 'string' || !win) return;
      await this.wm.open({ app: typeof row['open_app'] === 'string' && row['open_app'] ? row['open_app'] : this.desc.app, window: win, params: asObject(row['open_params']), parent: this.win, onResult: () => this.refreshSoon() });
      return;
    }
    if (cmd.kind === 'open' && cmd.opens) {
      const row = this.active().selectedRows()[0];
      const params: Record<string, unknown> = {};
      for (const [k, src] of Object.entries(cmd.opens.params)) {
        // '=hodnota' pevně, '$param' z parametrů tohoto okna (např. user ve vloženém okně), jinak sloupec vybraného řádku
        params[k] = src.startsWith('=') ? src.slice(1) : src.startsWith('$') ? this.desc.params[src.slice(1)] : row?.[src];
      }
      await this.wm.open({
        app: cmd.opens.app ?? this.desc.app,
        window: cmd.opens.window,
        params,
        modal: cmd.opens.modal,
        parent: this.win,
        onResult: () => this.refreshSoon(),
      });
      return;
    }
    // nebezpečný příkaz se vždy zeptá (i bez vlastního textu – kontrola 3. 11.: Cesty, Listiny mazaly bez dotazu)
    const ask = cmd.confirm ?? (cmd.danger ? 'core.cmd.confirm_delete' : null);
    if (ask && !(await confirm(tk(ask, { count: n }), { danger: cmd.danger, okLabel: cmd.danger ? tk(cmd.label) : undefined }))) return;
    try {
      const res = await runCommand(this.desc.app, this.desc.window, this.desc.params, cmd.id, this.selection);
      this.handleResult(res);
    } catch (e) {
      const err = e as ApiError;
      toast(tk(err.message, err.params ?? undefined), 'error');
    }
  }

  private handleResult(res: CommandResult): void {
    if (res.toast) toast(tk(res.toast.text, res.toast.params), res.toast.kind);
    if (res.refresh) void this.load(true);
    events.kick();
    if (res.open) void this.wm.open({ app: res.open.app ?? this.desc.app, window: res.open.window, params: res.open.params, modal: res.open.modal, parent: this.win, onResult: () => this.refreshSoon() });
    if (res.download) window.open(res.download, '_blank');
    if (res.close) this.host.close(res.result);
  }

  // ---- filtry

  /** Výchozí hodnoty filtrů (FilterDef.default). */
  private defaultFilters(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    // ano/ne jako '1' / '0' (stejně jako volba ve štítku), jinak by výchozí true vypadalo jako „Ne“
    for (const f of this.desc.filters) if (f.default !== undefined && f.default !== null && f.default !== '') out[f.key] = f.type === 'bool' && typeof f.default === 'boolean' ? (f.default ? '1' : '0') : f.default;
    return out;
  }

  /** Filtr je zapnutý jinak než výchozí (jen tehdy má smysl „Zrušit filtry“). */
  private changedFilters(): boolean {
    const def = this.defaultFilters();
    const keys = new Set([...Object.keys(def), ...Object.keys(this.state.filters)]);
    return Array.from(keys).some((k) => JSON.stringify(this.state.filters[k] ?? null) !== JSON.stringify(def[k] ?? null));
  }

  private clearFilters(): void {
    this.state.filters = this.defaultFilters();
    if (this.embedded && this.hiddenLayers.size) {
      this.hiddenLayers.clear();
      void savePref(`layers.${this.desc.app}.${this.desc.window}`, []);
    }
    this.state.page = 1;
    if (this.bar) this.renderFilterRow();
    else this.buildRibbon();
    this.renderChips();
    void this.load();
  }

  /** Štítky aktivních filtrů v hlavičce ribbonu: vidět, co je zapnuté, a zrušit jedním klikem. */
  private renderChips(): void {
    clear(this.chips);
    for (const f of this.desc.filters) {
      const v = this.state.filters[f.key];
      if (v === undefined || v === null || v === '') continue;
      let text: string;
      if (f.type === 'enum') text = tk(f.options?.find((o) => String(o.value) === String(v))?.label ?? String(v));
      else if (f.type === 'bool') text = v === '1' ? t('core.ui.yes') : t('core.ui.no');
      else if (f.type === 'daterange') {
        const r = v as { from?: string; to?: string };
        text = `${r.from ?? ''} – ${r.to ?? ''}`;
      } else text = String(v);
      const chip = h('button', { type: 'button', class: 'chip', title: t('core.ui.clear_filters') }, h('span', { class: 'chip-label' }, tk(f.label) + ': '), text, icon('x'));
      chip.addEventListener('click', () => {
        delete this.state.filters[f.key];
        this.state.page = 1;
        if (this.bar) this.renderFilterRow();
        else this.buildRibbon();
        this.renderChips();
        void this.load();
      });
      this.chips.appendChild(chip);
    }
  }

  private filterControl(f: FilterDef): HTMLElement {
    const apply = (value: unknown) => {
      if (value === '' || value === null || value === undefined) delete this.state.filters[f.key];
      else this.state.filters[f.key] = value;
      this.state.page = 1;
      this.renderChips();
      if (this.bar) this.renderFilterRow();
      void this.load();
    };
    const current = this.state.filters[f.key];
    switch (f.type) {
      case 'enum': {
        const sel = h('select', { class: 'input sm', 'aria-label': tk(f.label) }, h('option', { value: '' }, tk(f.label) + ': —'), (f.options ?? []).map((o) => h('option', { value: String(o.value), selected: String(current ?? '') === String(o.value) }, tk(o.label))));
        sel.addEventListener('change', () => apply(sel.value));
        return h('label', { class: 'filter' }, sel);
      }
      case 'bool': {
        const sel = h('select', { class: 'input sm' }, h('option', { value: '' }, tk(f.label) + ': —'), h('option', { value: '1', selected: current === '1' }, t('core.ui.yes')), h('option', { value: '0', selected: current === '0' }, t('core.ui.no')));
        sel.addEventListener('change', () => apply(sel.value));
        return h('label', { class: 'filter' }, sel);
      }
      case 'daterange': {
        const cur = (current as { from?: string; to?: string } | undefined) ?? {};
        const from = h('input', { class: 'input sm', type: 'date', value: cur.from ?? '', 'aria-label': tk(f.label) });
        const to = h('input', { class: 'input sm', type: 'date', value: cur.to ?? '' });
        const upd = () => apply(from.value || to.value ? { from: from.value, to: to.value } : null);
        from.addEventListener('change', upd);
        to.addEventListener('change', upd);
        return h('span', { class: 'filter range' }, from, h('span', { class: 'filter-dash' }, '–'), to);
      }
      default: {
        const inp = h('input', { class: 'input sm', type: 'text', placeholder: tk(f.label), value: typeof current === 'string' ? current : '' });
        inp.addEventListener('input', debounce(() => apply(inp.value), 450));
        return h('span', { class: 'filter' }, inp);
      }
    }
  }

  // ---- ovládání 'bar' (koncepce A, postup 60)

  private isMobile(): boolean {
    return this.wm.currentMode() === 'mobile';
  }

  private initBar(): void {
    const body = h('div', { class: 'list-body' });
    if (this.desc.detail) {
      this.detailSplit = new IwSplit();
      this.detailSplit.classList.add('list-detail-split', 'detail-off'); // zapne se, až je okno dost široké (updateDetailOn)
      this.detailSplit.first.appendChild(this.content);
      this.detailSplit.second.appendChild(this.detailPane);
      // šířka seznamu vedle detailu: řádky s vizitkou stačí úzké, tabulka potřebuje víc
      this.detailSplit.setWidth(Number(this.prefs()[`detailw.${this.desc.app}.${this.desc.window}`]) || (this.viewMode === 'cards' ? 380 : 560));
      this.detailSplit.onResize = (px) => void savePref(`detailw.${this.desc.app}.${this.desc.window}`, Math.round(px));
      body.appendChild(this.detailSplit);
    } else body.appendChild(this.content);
    const host = this.split ? this.split.second : null;
    if (host) {
      host.replaceChildren(body);
      // vysunutý panel na úzkém okně: klepnutí vedle ho zavře
      const backdrop = h('div', { class: 'side-backdrop' });
      backdrop.addEventListener('click', () => this.setSide(true));
      this.split?.second.appendChild(backdrop);
      // výběr adresáře na úzkém okně panel zavře (jako GNOME / Outlook)
      this.side.addEventListener('change', () => { if (this.narrow) window.setTimeout(() => this.setSide(true), 250); });
    }
    this.el.classList.add('chrome-bar');
    if (this.bar) this.bar.globalLabel = tk(this.desc.title);
    this.selAllBar.hidden = true;
    this.table.virtual = this.cards.virtual = true;
    const paged = new PagedRows<Row>({
      pageSize: this.desc.pageSize,
      rowId: this.desc.rowId,
      fetch: (page) => action<ListDataResponse>(this.desc.app, this.desc.window, 'data', this.desc.params, { ...this.dataReq(), page, pageSize: this.desc.pageSize } as unknown as Record<string, unknown>).then((r) => ({ rows: r.rows as Row[], total: r.total })),
      onUpdate: () => {
        this.active().updateRows(paged.rows);
        this.selection = this.active().selection();
        this.updateEnabled();
      },
      onTotalChanged: () => this.refreshSoon(), // přibylo / ubylo – načíst znovu se zachováním místa
      onError: (e) => toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'),
      onBusy: () => this.renderStatus(),
    });
    this.paged = paged;
    this.table.onRange = this.cards.onRange = (a, b) => paged.ensure(a, b);
    this.table.loadRange = this.cards.loadRange = (a, b) => paged.loadRange(a, b);
    this.initAz(this.detailSplit ? this.detailSplit.first : body);
    this.cards.tapOpens = this.table.tapOpens = this.isMobile();
    this.el.replaceChildren(this.filterRow, this.selAllBar, this.split ?? body, this.status, this.selBar, this.fab);
    this.fab.addEventListener('click', () => { const c = this.desc.commands.find((x) => x.primary); if (c) void this.execute(c); });
    this.observe(this.el, () => this.updateDetailOn());
    this.buildBar();
    this.renderFilterRow();
    this.renderHeader();
  }

  /** Příkaz potřebuje vybraný řádek? */
  private needsSelection(c: { enabled: string }): boolean {
    return c.enabled === 'selection' || c.enabled === 'single';
  }

  /** Položky lišty z příkazů okna + obecné (Přesunout do…, Obnovit) + zobrazení pod ⚙. */
  private buildBar(): void {
    const n = this.selection.length;
    const items: BarItem[] = this.desc.commands.map((c) => ({
      id: c.id,
      label: c.labelSelected && n ? tk(c.labelSelected, { count: n }) : tk(c.label),
      scope: c.labelSelected && n ? 'selection' : undefined,
      icon: c.icon,
      shortcut: c.shortcut,
      danger: c.danger,
      // hlavní (plné) tlačítko jen pro akci bez výběru – co potřebuje vybraný řádek, je akce nad výběrem (kontrola 3. 11.)
      place: c.primary && !this.needsSelection(c) ? 'primary' : c.tab && c.tab !== 'home' ? 'more' : c.enabled === 'selection' || c.enabled === 'single' ? 'selection' : 'action',
      // hned při sestavení (i hlavní akce): příkaz, který potřebuje vybraný řádek, bez výběru nejde
      disabled: !this.allowed(c),
      onClick: () => void this.execute(c),
    }));
    if (this.desc.layers?.move && this.desc.layers.items.some((i) => i.writable !== false)) items.push({ id: '__move', label: t('core.ui.move_to'), icon: 'folder', place: 'selection', onClick: () => void this.pickLayer() });
    if (!this.desc.commands.some((c) => c.id === 'refresh')) items.push({ id: '__refresh', label: t('core.cmd.refresh'), icon: 'refresh', shortcut: 'F5', place: 'more', onClick: () => void this.load(true) }); // stránka s vlastním Obnovit (Přehled) – ne podruhé
    if (this.desc.card) {
      const mode = this.viewMode === 'list' ? 'table' : this.cards.classList.contains('rows') ? 'rows' : 'grid';
      items.push(
        { id: '__v_rows', label: t('core.ui.view_rows'), icon: 'list', place: 'view', toggle: true, active: mode === 'rows', onClick: () => this.setBarView('rows') },
        { id: '__v_table', label: t('core.ui.view_table'), icon: 'table', place: 'view', toggle: true, active: mode === 'table', onClick: () => this.setBarView('table') },
        { id: '__v_grid', label: t('core.ui.view_cards'), icon: 'tiles', place: 'view', toggle: true, active: mode === 'grid', onClick: () => this.setBarView('grid') },
      );
    }
    // řazení i v řádcích a dlaždicích (v tabulce i klepnutím na hlavičku) – stejné místo ve všech oknech: ⚙ Řadit podle
    const sortable = this.desc.columns.filter((c) => c.sortable);
    if (sortable.length) {
      const cur = this.state.sort ?? this.desc.defaultSort;
      const set = (col: string, dir: 'asc' | 'desc') => this.setSort({ col, dir });
      items.push({
        id: '__sort', label: t('core.ui.sort_by'), icon: 'sort', place: 'view',
        menu: [
          ...sortable.map((c): MenuItem => ({ label: tk(c.label), checked: cur?.col === c.key, onSelect: () => set(c.key, cur?.col === c.key ? cur.dir : c.type === 'datetime' || c.type === 'date' || c.type === 'number' ? 'desc' : 'asc') })),
          { separator: true, label: '' },
          { label: t('core.ui.sort_asc'), checked: cur?.dir === 'asc', disabled: !cur, onSelect: () => { if (cur) set(cur.col, 'asc'); } },
          { label: t('core.ui.sort_desc'), checked: cur?.dir === 'desc', disabled: !cur, onSelect: () => { if (cur) set(cur.col, 'desc'); } },
        ],
      });
    }
    items.push({ id: '__select_mode', label: t('core.ui.select_mode'), icon: 'check-square', place: 'view', toggle: true, active: this.table.selectMode, onClick: () => this.setSelectMode(!this.table.selectMode) });
    if (this.viewMode === 'list') items.push({ id: '__columns', label: t('core.ui.columns'), icon: 'columns', place: 'view', menu: this.table.columnMenu() });
    this.barBase = items;
    this.bar?.setItems(this.composeBar(items));
    this.updateEnabled();
  }

  /**
   * Jedno záhlaví (vzor Outlook): s detailem jednoho záznamu nese i jeho akce – Nový · Upravit · akce nad výběrem ·
   * Smazat · ⋯; při úpravě v detailu jen Uložit · Použít · Zahodit změny. Stejné akce seznamu a detailu jen jednou.
   */
  private composeBar(list: BarItem[]): BarItem[] {
    const pi = this.detailOn && this.detailView && this.selection.length === 1 ? this.paneItems : null;
    if (!pi) return list;
    const rec = pi.map((b) => ({ ...b, id: 'r:' + b.id }));
    const view = list.filter((b) => b.place === 'view');
    if (pi.some((b) => b.id === '__save')) return [...rec.filter((b) => ['r:__save', 'r:__apply', 'r:__cancel'].includes(b.id)).map((b): BarItem => ({ ...b, place: b.id === 'r:__save' ? 'primary' : 'action' })), ...view];
    const labels = new Set([...rec.map((b) => b.label), ...this.paneQuick]);
    const edit = rec.filter((b) => b.id === 'r:__edit').map((b): BarItem => ({ ...b, place: 'selection' }));
    const del = rec.filter((b) => b.id === 'r:__delete').map((b): BarItem => ({ ...b, place: 'selection' }));
    const other = rec.filter((b) => b.id !== 'r:__edit' && b.id !== 'r:__delete').map((b): BarItem => ({ ...b, place: b.place === 'more' ? 'more' : 'selection' }));
    const sel = list.filter((b) => b.place === 'selection' && b.id !== this.desc.openCommand && b.id !== '__move' && !labels.has(b.label));
    const move = list.filter((b) => b.id === '__move');
    return [...list.filter((b) => b.place === 'primary'), ...edit, ...sel, ...del, ...move, ...other, ...list.filter((b) => b.place === 'action' || b.place === 'more' || b.place === 'view')];
  }

  /** Řazení z ⚙ (stejné jako klepnutí na hlavičku tabulky). */
  private setSort(sort: { col: string; dir: 'asc' | 'desc' }): void {
    this.state.sort = sort;
    this.state.page = 1;
    this.table.setSort(sort);
    this.buildBar();
    this.renderHeader();
    void this.load();
  }

  private setBarView(mode: 'rows' | 'table' | 'grid'): void {
    void savePref(`viewbar.${this.desc.app}.${this.desc.window}`, mode === 'table' ? 'list' : mode);
    this.cards.setLayout(mode === 'grid' ? 'grid' : 'rows');
    this.viewMode = mode === 'table' ? 'list' : 'cards';
    this.content.replaceChildren(this.active());
    this.buildBar();
    this.renderHeader();
    void this.load();
  }

  private setSelectMode(on: boolean): void {
    if (on && !this.wm.hasBack(this.backSel)) this.backSel = this.wm.pushBack(() => { this.backSel = 0; this.setSelectMode(false); return true; }, this.win);
    if (!on && this.backSel) {
      const id = this.backSel;
      this.backSel = 0;
      this.wm.dropBack(id);
    }
    this.table.setSelectMode(on);
    this.cards.setSelectMode(on);
    this.el.classList.toggle('select-mode', on);
    if (!on) this.active().clearSelection();
    this.buildBar();
    this.renderHeader();
  }

  /** Záhlaví okna: PC ☰ · název · lišta · hledání · ⚙; telefon ☰ · název · lupa · ⋮ (výběr: ✕ · počet · vybrat vše). */
  private renderHeader(): void {
    if (!this.bar) return;
    const mobile = this.isMobile();
    const selecting = mobile && this.table.selectMode;
    this.win.classList.toggle('sel-mode', selecting);
    this.win.classList.toggle('search-open', mobile && this.searchOpen);
    const start: Node[] = [];
    const main: Node[] = [];
    if (selecting) {
      start.push(headButton('x', t('core.ui.cancel'), () => this.setSelectMode(false)));
      main.push(h('span', { class: 'head-sel-count' }, t('core.ui.selected', { count: this.selection.length })), headButton('check-square', t('core.cmd.select_all'), () => this.active().selectAll()));
    } else if (mobile && this.searchOpen) {
      start.push(headButton('arrow-left', t('core.ui.close'), () => this.toggleSearch(false)));
      main.push(h('div', { class: 'search-wrap head-search' }, icon('search'), this.searchInput));
    } else {
      if (this.split && this.desc.layers) start.push(headButton('menu', tk(this.desc.layers.title), () => { const hide = !this.sideHidden; this.setSide(hide); if (!this.narrow) void savePref(`side.${this.desc.app}.${this.desc.window}`, hide); }, 'head-menu'));
      if (mobile) {
        main.push(h('span', { class: 'head-spacer' }));
        if (this.desc.searchable) main.push(headButton('search', t('core.cmd.search'), () => this.toggleSearch(true)));
        main.push(headButton('more-vertical', t('core.ui.menu'), (b) => IwMenu.showAt(this.kebabItems(), b)));
      } else {
        main.push(this.bar);
        if (this.desc.searchable) {
          main.push(h('div', { class: 'search-wrap head-search' }, icon('search'), this.searchInput));
          // úzké okno (< 640 px): hledání se rozbalí z lupy přes lištu
          main.push(headButton('search', t('core.cmd.search'), () => { this.win.classList.add('pc-search'); this.searchInput.focus(); }, 'head-search-btn'));
          this.searchInput.onblur = () => { if (!this.searchInput.value) this.win.classList.remove('pc-search'); };
        }
        const view = this.bar.all().filter((b) => b.place === 'view');
        if (view.length) main.push(headButton('settings', t('core.ui.view_settings'), (b) => IwMenu.showAt(view.map(barMenuItem), b), 'head-gear'));
      }
    }
    this.win.setHeader({ start, main });
    const primary = this.desc.commands.find((c) => c.primary && !this.needsSelection(c));
    this.fab.hidden = !mobile || !primary || selecting || !this.allowed(primary);
    if (primary) this.fab.replaceChildren(icon(primary.icon ?? 'plus'), h('span', null, tk(primary.label)));
    this.renderSelBar();
  }

  private toggleSearch(open: boolean): void {
    if (open && !this.wm.hasBack(this.backSearch)) this.backSearch = this.wm.pushBack(() => { this.backSearch = 0; this.toggleSearch(false); return true; }, this.win);
    if (!open && this.backSearch) {
      const id = this.backSearch;
      this.backSearch = 0;
      this.wm.dropBack(id);
    }
    this.searchOpen = open;
    if (!open && this.searchInput.value) {
      this.searchInput.value = '';
      this.state.search = '';
      void this.load();
    }
    this.renderHeader();
    if (open) this.searchInput.focus();
  }

  /** ⋮ na telefonu: všechny akce (bez hlavní – ta je plovoucí), méně časté, zobrazení, zavřít okno. */
  private kebabItems(): MenuItem[] {
    const all = this.bar?.all() ?? [];
    const out: MenuItem[] = all.filter((b) => (b.place === 'action' || b.place === 'selection') && !b.disabled).map(barMenuItem);
    const more = all.filter((b) => b.place === 'more').map(barMenuItem);
    const view = all.filter((b) => b.place === 'view' && b.id !== '__columns').map(barMenuItem);
    if (more.length) {
      if (out.length) out.push({ separator: true, label: '' });
      out.push(...more);
    }
    if (view.length) out.push(...(out.length ? [{ separator: true, label: '' }] : []), { header: true, label: t('core.ui.view_settings') }, ...view);
    out.push({ separator: true, label: '' }, { label: t('core.ui.minimize'), icon: 'minimize', onSelect: () => this.win.minimize() }, { label: t('core.ui.close_window'), icon: 'close', onSelect: () => void this.win.close() });
    return out;
  }

  /** Telefon v režimu výběru: hromadné akce dole v dosahu palce (max 4 + Další). */
  private renderSelBar(): void {
    const on = this.isMobile() && this.table.selectMode && this.selection.length > 0;
    this.selBar.hidden = !on;
    if (!on) return;
    const acts = (this.bar?.all() ?? []).filter((b) => b.place === 'selection' && !b.disabled);
    const shown = acts.slice(0, acts.length > 4 ? 3 : 4);
    const rest = acts.slice(shown.length);
    const btn = (label: string, ic: string, fn: (el: HTMLButtonElement) => void, danger = false) => {
      const b = h('button', { type: 'button', class: 'sel-act' + (danger ? ' danger' : '') }, icon(ic), h('span', null, label)) as HTMLButtonElement;
      b.addEventListener('click', () => fn(b));
      return b;
    };
    this.selBar.replaceChildren(...shown.map((b) => btn(b.label, b.icon ?? 'check', (el) => { if (b.menu) IwMenu.showAt(b.menu, el); else b.onClick?.(); }, b.danger)), ...(rest.length ? [btn(t('core.ui.more'), 'more', (el) => IwMenu.showAt(rest.map(barMenuItem), el))] : []));
  }

  private onBarSelect(ids: string[]): void {
    const mobile = this.isMobile();
    if (mobile && this.table.selectMode) {
      if (!ids.length) this.setSelectMode(false);
      else this.renderHeader();
      return;
    }
    if (!mobile) this.showDetailSoon(); // telefon: klepnutí otevírá záznam (IwCards.tapOpens)
    if (this.desc.commands.some((c) => c.labelSelected)) this.buildBar(); // popisky podle výběru (Exportovat vybrané)
  }

  /** Detail vpravo jen v širokém okně (ne telefon); jinak dvojklik otevře okno záznamu jako dosud. */
  private updateDetailOn(): void {
    if (!this.detailSplit) return;
    const on = !this.isMobile() && this.el.clientWidth >= 860;
    if (on === this.detailOn) return;
    this.detailOn = on;
    this.detailSplit.classList.toggle('detail-off', !on);
    if (on) this.showDetailSoon();
  }

  private async syncDetail(): Promise<void> {
    const D = this.desc.detail;
    if (!D || !this.detailOn) return;
    const id = this.selection.length === 1 ? (this.selection[0] ?? null) : null;
    if (id !== null && id === this.detailId) return;
    if (id === null && this.detailId === null && this.detailPane.firstChild) {
      this.detailPane.replaceChildren(emptyState(this.selection.length > 1 ? t('core.ui.selected', { count: this.selection.length }) : t('core.ui.pick_record'), this.desc.icon));
      return;
    }
    if (this.detailBusy) { // běží dotaz na neuložené změny – rozhodne se po něm
      this.showDetailSoon();
      return;
    }
    if (this.detailView?.canClose) {
      this.detailBusy = true;
      let ok: boolean;
      try {
        ok = await this.detailView.canClose();
      } finally {
        this.detailBusy = false;
      }
      if (!ok) { // rozpracovaná úprava zůstane – a výběr v seznamu se vrátí na ni (detail i akce v záhlaví patří jí)
        if (this.detailId) this.active().select([this.detailId]);
        return;
      }
    }
    this.detailView?.destroy?.();
    this.detailView = null;
    this.paneItems = null;
    this.bar?.setItems(this.composeBar(this.barBase));
    this.updateEnabled(); // akce seznamu (Otevřít…) podle nového výběru
    this.detailId = id;
    const seq = ++this.detailSeq;
    if (id === null) {
      this.detailPane.replaceChildren(emptyState(this.selection.length > 1 ? t('core.ui.selected', { count: this.selection.length }) : t('core.ui.pick_record'), this.desc.icon));
      return;
    }
    this.detailPane.replaceChildren(spinner());
    try {
      const d = await action<WindowDescriptor>(D.app ?? this.desc.app, D.window, 'describe', { ...(D.params ?? {}), [D.param]: id }); // D.params: režim operátora (_customer)
      if (seq !== this.detailSeq) return;
      const view = createView(d, this.win, this.wm, {
        pane: true,
        paneItems: (items, quick) => {
          if (seq !== this.detailSeq) return;
          this.paneItems = items;
          this.paneQuick = quick ?? [];
          this.bar?.setItems(this.composeBar(this.barBase));
          this.updateEnabled();
        },
        close: (r) => {
          if (r === 'deleted') {
            this.detailId = null;
            this.detailView = null;
            this.paneItems = null;
            this.bar?.setItems(this.composeBar(this.barBase));
            this.detailPane.replaceChildren(emptyState(t('core.ui.pick_record'), this.desc.icon));
            this.active().select([]); // smazaný záznam už není co mít vybrané
          }
          this.refreshSoon();
        },
        result: () => this.refreshSoon(),
      });
      this.detailView = view;
      this.detailPane.replaceChildren(view.el);
      this.bar?.setItems(this.composeBar(this.barBase)); // detail poslal akce už při vzniku
      this.updateEnabled();
    } catch (e) {
      if (seq !== this.detailSeq) return;
      const err = e as ApiError;
      this.detailId = null;
      if (err.status === 404) { // záznam mezitím zmizel (smazal ho někdo jiný) – pryč i z výběru
        this.active().select(this.selection.filter((x) => x !== id));
        events.kick();
        return;
      }
      this.detailPane.replaceChildren(errorState(tk(err.message, err.params ?? undefined), () => void this.syncDetail(), err.id));
    }
  }

  /** Štítky nad seznamem: adresáře (vrstvy) a filtry – klepnutí rozbalí výběr, aktivní je zvýrazněný (vzor Outlook web). */
  private renderFilterRow(): void {
    if (!this.bar) return;
    clear(this.filterRow);
    const chip = (id: string, label: string, active: boolean, onClick: (el: HTMLElement) => void, colorDot?: string | null) => {
      const b = h('button', { type: 'button', class: 'fchip' + (active ? ' active' : ''), dataset: { id } }, colorDot ? h('span', { class: 'layer-dot', style: { background: colorDot } }) : null, h('span', { class: 'fchip-label' }, label), icon('chevron-down', 'icon fchip-caret'));
      b.addEventListener('click', () => onClick(b));
      this.filterRow.appendChild(b);
    };
    const L = this.desc.layers;
    if (L) {
      const shown = L.items.filter((i) => !this.hiddenLayers.has(i.value));
      const label = shown.length === L.items.length ? tk(L.title) + ': ' + t('core.ui.all_short') : shown.length === 0 ? tk(L.title) + ': —' : shown.slice(0, 2).map((i) => i.label).join(', ') + (shown.length > 2 ? ` +${shown.length - 2}` : '');
      chip('__layers', label, shown.length < L.items.length, (el) => this.layersMenu(el));
    }
    let any = false;
    for (const f of this.desc.filters) {
      const v = this.state.filters[f.key];
      const active = v !== undefined && v !== null && v !== '';
      any ||= active;
      let text = tk(f.label);
      if (active) {
        if (f.type === 'enum') text += ': ' + tk(f.options?.find((o) => String(o.value) === String(v))?.label ?? String(v));
        else if (f.type === 'bool') text += ': ' + (v === '1' ? t('core.ui.yes') : t('core.ui.no'));
        else if (f.type === 'daterange') { const r = v as { from?: string; to?: string }; text += `: ${r.from ?? ''} – ${r.to ?? ''}`; }
        else text += ': ' + String(v);
      }
      chip('__f_' + f.key, text, active, (el) => this.filterPick(f, el));
    }
    if (any && this.changedFilters()) {
      const clr = h('button', { type: 'button', class: 'fchip clear', title: t('core.ui.clear_filters') }, icon('x'), h('span', { class: 'fchip-label' }, t('core.ui.clear_filters')));
      clr.addEventListener('click', () => this.clearFilters());
      this.filterRow.appendChild(clr);
    }
    this.filterRow.hidden = !this.filterRow.childElementCount;
  }

  /** Výběr hodnoty filtru ze štítku: výčet a ano/ne jako nabídka, text a období jako panel s polem. */
  private filterPick(f: FilterDef, anchor: HTMLElement): void {
    const set = (value: unknown) => {
      if (value === '' || value === null || value === undefined) delete this.state.filters[f.key];
      else this.state.filters[f.key] = value;
      this.state.page = 1;
      this.renderFilterRow();
      void this.load();
    };
    const cur = this.state.filters[f.key];
    if (f.type === 'enum' || f.type === 'bool') {
      const opts = f.type === 'bool' ? [{ value: '1', label: t('core.ui.yes') }, { value: '0', label: t('core.ui.no') }] : (f.options ?? []).map((o) => ({ value: String(o.value), label: tk(o.label) }));
      IwMenu.showAt([{ label: t('core.ui.all_short'), checked: cur === undefined || cur === '', onSelect: () => set('') }, { separator: true, label: '' }, ...opts.map((o) => ({ label: o.label, checked: String(cur ?? '') === o.value, onSelect: () => set(o.value) }))], anchor);
      return;
    }
    popover(anchor, [this.filterControl(f)]).querySelector<HTMLInputElement>('input')?.focus();
  }

  // ---- data

  /** keep = obnovit se zachováním místa (živé změny, F5); jinak od začátku (filtr, hledání, řazení). */
  async load(keep = false): Promise<void> {
    if (this.loading) {
      this.refreshSoon();
      return;
    }
    this.loading = true;
    this.el.classList.add('loading');
    const seq = ++this.loadSeq;
    const prevTotal = this.total;
    const req: ListDataRequest = { ...this.dataReq(), page: this.bar ? 1 : this.state.page, pageSize: this.desc.pageSize };
    try {
      const res = await action<ListDataResponse>(this.desc.app, this.desc.window, 'data', this.desc.params, req as unknown as Record<string, unknown>);
      this.total = res.total;
      if (res.rows.length === 0 && res.page > 1) {
        this.state.page = Math.max(1, Math.ceil(res.total / res.pageSize));
        this.loading = false;
        this.el.classList.remove('loading');
        return void this.load();
      }
      if (this.bar) {
        // plynulý seznam: celková délka hned, nenačtené jako zástupci; dávky se dočtou podle posunu (onRange).
        // Obnovení (živá změna, F5) nechá dosavadní řádky na místě a viditelné dávky jen vymění – seznam nezešedne.
        const paged = this.paged as PagedRows<Row>;
        paged.reset(res.total, res.rows as Row[], keep);
        if (!keep) {
          this.active().clearSelection();
          this.active().scrollToIndex(0);
        }
        this.active().setRows(paged.rows, this.desc.rowId);
        if (!keep || res.total !== prevTotal) void this.loadIndex(seq);
        if (keep) void this.pruneSelection(seq, res.rows as Row[]);
      } else this.active().setRows(res.rows as Row[], this.desc.rowId);
      this.selection = this.active().selection();
      this.updateEnabled(); // řádky se mohly změnit (stav služby) – podmínky příkazů znovu
      if (this.bar) this.showDetailSoon();
      this.renderStatus();
      if (this.bar && res.total === 0) this.renderEmpty();
      else if (this.content.firstChild !== this.active()) this.content.replaceChildren(this.active());
      this.el.classList.toggle('is-empty', Boolean(this.bar) && res.total === 0);
    } catch (e) {
      const err = e as ApiError;
      this.content.replaceChildren(errorState(tk(err.message), () => void this.load(true), err.id));
    } finally {
      this.loading = false;
      this.el.classList.remove('loading');
    }
  }

  /**
   * Po obnovení: vybrané řádky, které už neexistují (smazané, mimo filtr), z výběru pryč. Co je v první dávce, je jisté;
   * zbytek ověří seznam id ze serveru (jen když je něco vybrané mimo ni).
   */
  private async pruneSelection(seq: number, fresh: Row[]): Promise<void> {
    const sel = this.active().selection();
    const have = new Set(fresh.map((r) => String(r[this.desc.rowId])));
    if (!sel.length || sel.every((id) => have.has(id))) return;
    let keep = have;
    if (this.total <= 5000) {
      try {
        keep = new Set((await action<{ ids: string[] }>(this.desc.app, this.desc.window, 'ids', this.desc.params, this.dataReq() as unknown as Record<string, unknown>)).ids);
      } catch {
        return; // nevadí, zkusí se při dalším obnovení
      }
    }
    if (seq !== this.loadSeq) return;
    this.active().select(this.active().selection().filter((id) => keep.has(id)));
  }

  /** Prázdný seznam: jeden stav přes celé okno s cestou dál (hlavní akce, vymazat hledání / filtry). */
  private renderEmpty(): void {
    const filtered = Boolean(this.state.search) || this.changedFilters() || this.hiddenLayers.size > 0;
    const acts: HTMLElement[] = [];
    const btn = (label: string, ic: string, fn: () => void, primary = false) => {
      const b = h('button', { type: 'button', class: 'btn' + (primary ? ' primary' : '') }, icon(ic), label);
      b.addEventListener('click', fn);
      acts.push(b);
    };
    if (this.state.search) btn(t('core.ui.clear_search'), 'x', () => { this.searchInput.value = ''; this.state.search = ''; void this.load(); });
    else if (filtered) btn(t('core.ui.clear_filters'), 'x', () => this.clearFilters());
    const primary = this.desc.commands.find((c) => c.primary && !this.needsSelection(c)); // prázdný stav: hlavní akce bez výběru
    if (primary && this.allowed(primary)) btn(tk(primary.label), primary.icon ?? 'plus', () => void this.execute(primary), true);
    const text = this.state.search ? t('core.ui.nothing_found', { q: this.state.search }) : t('core.ui.empty');
    const st = emptyState(text, this.desc.icon);
    if (acts.length) st.appendChild(h('div', { class: 'list-empty-actions' }, acts));
    this.content.replaceChildren(st);
  }

  /** Hledání, filtry, řazení a vrstvy pro dotaz na server (data, ids, index). */
  private dataReq(): ListDataRequest {
    const L = this.desc.layers;
    const layers = L && this.hiddenLayers.size ? L.items.map((i) => i.value).filter((v) => !this.hiddenLayers.has(v)) : undefined;
    return { page: 1, pageSize: this.desc.pageSize, search: this.state.search || undefined, filters: this.state.filters, sort: this.state.sort ?? undefined, layers };
  }

  /** Vybráno vše načtené → nabídnout výběr všech (id ze serveru, do 5000 – jako Gmail). */
  private renderSelectAll(): void {
    const n = this.selection.length;
    const loaded = this.paged ? this.paged.loaded().length : 0;
    const all = Boolean(this.bar) && n > 1 && n >= this.total && this.total > loaded;
    const on = Boolean(this.bar) && n > 1 && n >= loaded && this.total > n;
    this.selAllBar.hidden = !on && !all;
    if (all) {
      const clr = h('button', { type: 'button', class: 'btn sm' }, t('core.ui.clear_selection'));
      clr.addEventListener('click', () => this.active().clearSelection());
      this.selAllBar.replaceChildren(icon('check-square'), h('span', null, t('core.ui.selected_all', { count: this.total })), clr);
      return;
    }
    if (!on) return;
    const btn = h('button', { type: 'button', class: 'btn sm' }, t('core.ui.select_all_n', { count: this.total }));
    btn.disabled = this.total > 5000;
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      try {
        const r = await action<{ ids: string[] }>(this.desc.app, this.desc.window, 'ids', this.desc.params, this.dataReq() as unknown as Record<string, unknown>);
        this.active().selectIds(r.ids);
      } catch (e) {
        toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
      }
    });
    this.selAllBar.replaceChildren(icon('check-square'), h('span', null, t('core.ui.selected_of', { count: n, total: this.total })), btn);
  }

  // ---- abeceda A–Z (Kontakty): skok na písmeno, na telefonu tah prstem s velkým písmenem

  private static LETTERS = ['A', 'B', 'C', 'Č', 'D', 'E', 'F', 'G', 'H', 'CH', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'Ř', 'S', 'Š', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'Ž', '#'];

  /** Abeceda má smysl jen při řazení podle jejího sloupce vzestupně. */
  private alphabetOn(): boolean {
    const key = this.desc.alphabet;
    if (!key || !this.bar) return false;
    const sort = this.state.sort ?? this.desc.defaultSort;
    return sort !== null && sort.col === key && sort.dir === 'asc';
  }

  private async loadIndex(seq: number): Promise<void> {
    if (!this.alphabetOn() || this.total < 20) {
      this.azEl.hidden = true;
      return;
    }
    try {
      const r = await action<{ index: Record<string, number> }>(this.desc.app, this.desc.window, 'index', this.desc.params, this.dataReq() as unknown as Record<string, unknown>);
      if (seq !== this.loadSeq) return;
      this.azIndex = r.index;
      this.renderAz();
    } catch {
      this.azEl.hidden = true;
    }
  }

  private renderAz(): void {
    this.azEl.hidden = false;
    this.placeAz();
    this.azEl.replaceChildren(...ListView.LETTERS.map((l) => h('span', { class: 'az-l' + (l in this.azIndex ? '' : ' off'), dataset: { l } }, l)));
  }

  /** Písmeno pod prstem / myší → skok (bez písmene na nejbližší další, které existuje). */
  private azJump(clientY: number): void {
    const els = Array.from(this.azEl.querySelectorAll<HTMLElement>('.az-l'));
    let hit: HTMLElement | undefined;
    for (const el of els) {
      const r = el.getBoundingClientRect();
      if (clientY >= r.top && clientY <= r.bottom) hit = el;
    }
    if (!hit) return;
    const letters = ListView.LETTERS;
    const from = letters.indexOf(hit.dataset['l'] ?? '');
    let target: number | undefined;
    for (let i = from; i < letters.length && target === undefined; i++) target = this.azIndex[letters[i] as string];
    if (target === undefined) return;
    this.azBubble.textContent = hit.dataset['l'] ?? '';
    this.azBubble.hidden = false;
    this.azBubble.style.top = hit.getBoundingClientRect().top - this.azEl.getBoundingClientRect().top + 'px';
    this.active().scrollToIndex(target);
  }

  /** Abeceda těsně vlevo od posuvníku (šířka posuvníku se liší podle prohlížeče; na telefonu je překryvný = 0). */
  private placeAz(): void {
    const a = this.active();
    const s = a === this.table ? a.querySelector<HTMLElement>('.table-scroll') : a;
    const sbw = s ? s.offsetWidth - s.clientWidth : 0;
    // překryvný posuvník (Windows 11, macOS…) má šířku 0, ale při najetí se ukáže u okraje – i tak mu nechat místo
    this.azEl.style.right = (this.isMobile() ? 2 : Math.max(12, sbw) + 2) + 'px';
    this.azEl.classList.toggle('az-cramped', (this.azEl.parentElement?.clientHeight ?? 999) < 380); // 32 písmen se nevejde
  }

  private initAz(host: HTMLElement): void {
    this.observe(host, () => { if (!this.azEl.hidden) this.placeAz(); });
    this.azEl.hidden = true;
    this.azBubble.hidden = true;
    host.classList.add('az-host');
    host.append(this.azEl, this.azBubble);
    this.azEl.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.azEl.setPointerCapture(e.pointerId);
      this.azJump(e.clientY);
      const move = (ev: PointerEvent) => this.azJump(ev.clientY);
      const up = () => {
        this.azEl.removeEventListener('pointermove', move);
        this.azEl.removeEventListener('pointerup', up);
        this.azEl.removeEventListener('pointercancel', up);
        window.setTimeout(() => { this.azBubble.hidden = true; }, 400);
      };
      this.azEl.addEventListener('pointermove', move);
      this.azEl.addEventListener('pointerup', up);
      this.azEl.addEventListener('pointercancel', up);
    });
  }

  private renderStatus(): void {
    if (this.bar) {
      // úzké okno bez detailu: nápověda, jak záznam otevřít
      const hint = this.desc.detail && !this.detailOn && !this.isMobile() && this.selection.length === 1 ? h('span', { class: 'muted' }, t('core.ui.open_hint')) : '';
      this.status.replaceChildren(statusBar(t('core.ui.records', { count: this.total }), this.selection.length ? t('core.ui.selected', { count: this.selection.length }) : '', this.paged?.busy() ? h('span', { class: 'muted' }, t('core.ui.loading_more')) : hint));
      this.renderSelectAll();
      return;
    }
    const pages = Math.max(1, Math.ceil(this.total / this.desc.pageSize));
    const prev = h('button', { type: 'button', class: 'btn ghost sm', title: t('core.ui.prev') }, icon('chevron-left'));
    const next = h('button', { type: 'button', class: 'btn ghost sm', title: t('core.ui.next') }, icon('chevron'));
    prev.disabled = this.state.page <= 1;
    next.disabled = this.state.page >= pages;
    prev.addEventListener('click', () => {
      this.state.page--;
      void this.load();
    });
    next.addEventListener('click', () => {
      this.state.page++;
      void this.load();
    });
    this.status.replaceChildren(
      statusBar(
        t('core.ui.records', { count: this.total }),
        this.selection.length ? t('core.ui.selected', { count: this.selection.length }) : '',
        h('span', { class: 'pager' }, prev, t('core.ui.page', { page: this.state.page, pages }), next),
      ),
    );
  }

  refresh(): void {
    void this.load(true);
  }

  setMode(mode: 'pc' | 'tablet' | 'mobile'): void {
    if (this.bar) {
      this.cards.tapOpens = this.table.tapOpens = mode === 'mobile';
      this.renderHeader();
      this.updateDetailOn();
      return;
    }
    this.ribbon.setMode(mode);
  }

  getState(): unknown {
    return { ...this.state };
  }

  setState(state: unknown): void {
    if (!state || typeof state !== 'object') return;
    const s = state as Partial<ListState>;
    this.state = { page: s.page ?? 1, sort: s.sort ?? this.desc.defaultSort, search: s.search ?? '', filters: asObject(s.filters) };
    this.searchInput.value = this.state.search;
    this.table.setSort(this.state.sort);
    if (this.bar) this.renderFilterRow();
    else this.buildRibbon();
    this.renderChips();
    void this.load();
  }

  focus(): void {
    this.active().focus();
  }

  /** Okno seznamu s rozpracovanou úpravou v detailu: stejný dotaz jako samostatné okno záznamu. */
  async canClose(): Promise<boolean> {
    return this.detailView?.canClose ? this.detailView.canClose() : true;
  }

  private observe(el: HTMLElement, fn: () => void): void {
    const ro = new ResizeObserver(fn);
    ro.observe(el);
    this.observers.push(ro);
  }

  destroy(): void {
    for (const ro of this.observers) ro.disconnect();
    this.detailView?.destroy?.();
    this.showDetailSoon.cancel();
    this.unsubscribe?.();
    this.refreshSoon.cancel();
    this.describeSoon.cancel();
    this.ro?.disconnect();
  }
}
